import { CalendarClock, ExternalLink, KeyRound, Lock, Radio, Video } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { useCourseSessions } from '@/features/catalog/sessions'
import { cn } from '@/lib/cn'
import type { ClassSession, MeetingProvider } from '@/lib/types'

const providerLabel: Record<MeetingProvider, string> = {
  google_meet: 'Google Meet',
  zoom: 'Zoom',
  microsoft_teams: 'Microsoft Teams',
  other: 'Ruang daring',
}

/** Waktu ditulis lengkap beserta zonanya supaya tidak ada yang salah hadir. */
function formatSchedule(starts: string, ends: string): string {
  const mulai = new Date(starts)
  const selesai = new Date(ends)

  const tanggal = mulai.toLocaleDateString('id-ID', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
  const jam = (d: Date) => d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })
  const zona = new Intl.DateTimeFormat('id-ID', { timeZoneName: 'short' })
    .formatToParts(mulai)
    .find((part) => part.type === 'timeZoneName')?.value

  return `${tanggal} · ${jam(mulai)}–${jam(selesai)}${zona ? ` ${zona}` : ''}`
}

function statusBadge(session: ClassSession) {
  switch (session.status) {
    case 'live':
      return <Badge tone="success" icon={<Radio className="size-3.5" />}>Sedang berlangsung</Badge>
    case 'done':
      return <Badge tone="neutral">Selesai</Badge>
    case 'canceled':
      return <Badge tone="danger">Dibatalkan</Badge>
    default:
      return <Badge tone="info" icon={<CalendarClock className="size-3.5" />}>Terjadwal</Badge>
  }
}

/**
 * ClassSessionList menampilkan jadwal pertemuan daring sebuah kelas.
 *
 * Tautan ruang rapat hanya ada pada data bila peladen memang mengirimkannya;
 * komponen ini tidak pernah menyembunyikan tautan yang sudah diterimanya,
 * karena penyembunyian di antarmuka bukan pengamanan.
 */
export function ClassSessionList({ slug, className }: { slug: string; className?: string }) {
  const { data: sessions, isLoading } = useCourseSessions(slug)

  if (isLoading) {
    return (
      <div className={cn('space-y-3', className)}>
        {Array.from({ length: 2 }).map((_, index) => (
          <Skeleton key={index} className="h-28 rounded-2xl" />
        ))}
      </div>
    )
  }

  if (!sessions || sessions.length === 0) {
    return (
      <EmptyState
        className={className}
        icon={<CalendarClock className="size-6" />}
        title="Belum ada sesi terjadwal"
        description="Pengajar akan mengumumkan jadwal pertemuan daring di sini."
      />
    )
  }

  return (
    <ul className={cn('space-y-3', className)}>
      {sessions.map((session) => (
        <li key={session.id} className="card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-bold text-ink">{session.title}</h3>
                {statusBadge(session)}
              </div>
              <p className="mono-label mt-1.5 text-[11px] text-subtle">
                {formatSchedule(session.starts_at, session.ends_at)} · {session.duration_minutes} menit
              </p>
              {session.description ? (
                <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-muted">
                  {session.description}
                </p>
              ) : null}
            </div>

            <span className="mono-label inline-flex shrink-0 items-center gap-1.5 rounded-full bg-sunken px-3 py-1.5 text-[11px] text-muted">
              <Video className="size-3.5" />
              {providerLabel[session.provider]}
            </span>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            {session.meeting_url ? (
              <>
                <a href={session.meeting_url} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant={session.can_join ? 'primary' : 'outline'}>
                    {session.can_join ? 'Masuk ruang sekarang' : 'Buka tautan rapat'}
                    <ExternalLink className="size-3.5" />
                  </Button>
                </a>
                {session.meeting_passcode ? (
                  <span className="mono-label inline-flex items-center gap-1.5 text-[11px] text-muted">
                    <KeyRound className="size-3.5" />
                    Sandi: {session.meeting_passcode}
                  </span>
                ) : null}
              </>
            ) : (
              // Kalimatnya dibuat tegas: yang belum terdaftar perlu tahu
              // bahwa tautannya memang ada, hanya belum untuk mereka.
              <span className="inline-flex items-center gap-2 text-sm text-subtle">
                <Lock className="size-4" />
                Tautan ruang rapat terbuka untuk peserta kelas ini.
              </span>
            )}

            {session.recording_url ? (
              <a
                href={session.recording_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mono-label inline-flex items-center gap-1.5 text-[11px] text-brand hover:underline"
              >
                <Video className="size-3.5" />
                Rekaman
              </a>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  )
}
